import Papa from "papaparse";
import React, { useState, useEffect } from "react";
import { Tooltip } from "react-tippy";
import Swal from "sweetalert2";
import Excel from "exceljs";

import { ErrorBoundary } from "storybook-dashboard/errors";
import Im, { set } from "immutable";
import { doFetch, fetcher } from "storybook-dashboard/utils/fetching";
import { mergeNodes } from "storybook-dashboard/utils";
import Octicon from "react-octicon";
import UploadFileDragDrop from "AppSrc/utils/dragDropUpload";
import { getExcelData } from "./parseexcel";

export const HeaderButtons = (props) => {
  let { readOnly, formTemplate, setTableVisible, closeModal, dirty, data } = props;
  const [selectedMethod, setSelectedMethod] = useState(null); // 'manual' or 'upload'
  const [step, setStep] = useState(1);

  console.log(data?.toJS(), "data in headerButtons");
  useEffect(() => {
    if (data && data.size > 0 && !data.get(0).isEmpty()) {
      setStep(4);
    } // if theres data, start at step 4
  }, [data]);

  // Update tableVisible based on step and data
  useEffect(() => {
    let shouldShowTable = false;

    // Always show the table if there is actual data, not a placeholder and set
    // step to 4 if so. This needs to be a separate check as setStep won't alter
    // the value of step until after this hook completes.
    if (data.size > 0 && !isEmptyOrAllNull(data.get(0))) {
      shouldShowTable = true;
      setStep(4);
    }

    if (step === 4) {
      // Always show table in step 4 (view/edit data) regardless of method
      // This ensures the table is visible even after clearing data
      shouldShowTable = true;
    }

    // Pass tableVisible to parent component
    if (setTableVisible) {
      setTableVisible(shouldShowTable);
    }
  }, [step, selectedMethod, data, setTableVisible]);

  // Confirmation dialog for going back or switching methods
  const showConfirmationDialog = (actionText, onConfirm) => {
    const hasData = data && data.size > 0;
    if (hasData || dirty) {
      Swal.fire({
        title: "Are you sure?",
        text: `${actionText} will discard all current data. Are you sure?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, start over",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          onConfirm();
        }
      });
    } else {
      onConfirm();
    }
  };

  // Handle going back to step 1
  const handleBackToStep1 = () => {
    showConfirmationDialog("Going back", () => {
      setStep(1);
      setSelectedMethod(null);
      // Clear data if needed
      if (props.setData) {
        props.setData(Im.List());
      }
    });
  };

  // Handle modal close with confirmation if there are unsaved changes
  const handleCloseModal = () => {
    if (dirty) {
      Swal.fire({
        title: "Are you sure?",
        text: "You have unsaved changes. Are you sure you want to close?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, close",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed && closeModal) {
          closeModal();
        }
      });
    } else {
      if (closeModal) closeModal();
    }
  };

  // Add close button event listener if modal exists
  useEffect(() => {
    const modalElement = document.querySelector(".modal");
    if (modalElement && closeModal) {
      const closeButtons = modalElement.querySelectorAll('[data-dismiss="modal"], .btn-close, .close');
      closeButtons.forEach((button) => {
        button.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          handleCloseModal();
        });
      });

      return () => {
        closeButtons.forEach((button) => {
          button.removeEventListener("click", handleCloseModal);
        });
      };
    }
  }, [dirty, closeModal]);

  // Progress Tracker Component
  const ProgressTracker = ({ currentStep }) => {
    const steps = [
      { step: 1, label: "Choose method" },
      { step: 2, label: "Download template" },
      { step: 3, label: "Upload file" },
      { step: 4, label: "View & edit data" },
    ];

    return (
      <div className="w-100 mb-4 px-3">
        <div className="d-flex justify-content-between align-items-center position-relative">
          {steps.map((stepInfo, index) => {
            const isActive = stepInfo.step === currentStep;
            const isCompleted = stepInfo.step < currentStep;
            const isAccessible = stepInfo.step <= currentStep || (selectedMethod === "manual" && stepInfo.step === 4);

            return (
              <div
                key={stepInfo.step}
                className="d-flex flex-column align-items-center position-relative"
                style={{ zIndex: 3, minWidth: "80px" }}
              >
                {/* Step circle */}
                <div
                  className={`rounded-circle d-flex align-items-center justify-content-center text-white font-weight-bold ${
                    isActive ? "bg-primary" : isCompleted ? "bg-success mb-4" : "bg-light text-muted"
                  }`}
                  style={{ width: "40px", height: "40px", fontSize: "14px" }}
                  aria-current={isActive ? "step" : undefined}
                  aria-label={`Step ${stepInfo.step}: ${stepInfo.label}${
                    isActive ? " (current)" : isCompleted ? " (completed)" : ""
                  }`}
                >
                  {isCompleted ? <Octicon name="check" /> : stepInfo.step}
                </div>

                {/* Step label */}
                <h6
                  className={`mt-2 text-center font-weight-bold ${
                    isActive ? "text-primary " : isCompleted ? "text-success" : "text-muted"
                  }`}
                >
                  {!isCompleted && stepInfo.label}
                </h6>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (readOnly) {
    return null;
  }

  let templateLink = formTemplate?.get("url");
  let templateName = formTemplate?.get("name");

  // Step 1: Method Selection
  if (step === 1) {
    return (
      <div>
        <ProgressTracker currentStep={1} />
        <div className="">
          <div className=" text-center py-5">
            <div className="mb-4">
              <h4 className=" mb-3">How would you like to enter your data?</h4>
            </div>

            <div className="row justify-content-center">
              <div className="col-md-5 mb-3">
                <div
                  className="card h-100 border-dark shadow-sm"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setSelectedMethod("manual");
                    setStep(4); // Skip to step 4 for manual entry
                  }}
                >
                  <div className="card-body d-flex flex-column justify-content-around align-items-center py-4">
                    <h5 className="card-title text-dark">Manual entry or edit</h5>
                    <p className="card-text text-muted text-center">
                      Enter or <b>EDIT any uploaded</b> data directly into the table using the form interface
                    </p>
                    <button className="btn btn-dark">
                      <Octicon name="keyboard" /> Choose manual
                    </button>
                  </div>
                </div>
              </div>

              <div className="col-md-5 mb-3">
                <div
                  className="card h-100 border-primary border-2 shadow-sm"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setSelectedMethod("upload");
                    setStep(2); // Continue to step 2 for upload
                  }}
                >
                  <div className="card-body d-flex flex-column justify-content-around align-items-center py-4">
                    <h5 className="card-title text-primary">Upload via CSV</h5>
                    <p className="card-text text-muted text-center">
                      Download template, fill it out in Excel/CSV, then upload your completed <b>file as a CSV.</b>
                    </p>
                    <button className="btn btn-primary">
                      <Octicon name="cloud-upload" className="mr-2" />
                      Choose upload
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Download Template
  if (step === 2) {
    return (
      <div>
        <ProgressTracker currentStep={2} />
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="m-2">Download the template</h5>
        </div>
        <div className="text-center py-4">
          <DownloadTemplateButton templateLink={templateLink} templateName={templateName} />
          <h6 className="text-muted mt-3 mb-5">
            Use this template and save only the relevant sheet as a CSV for upload.
          </h6>

          <div className="d-flex justify-content-between">
            <button className="btn btn-outline-secondary" onClick={handleBackToStep1}>
              <Octicon name="chevron-left" className="mr-2" />
              Back
            </button>
            <button className="btn btn-outline-primary" onClick={() => setStep(3)}>
              Next: Upload file
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Upload File
  if (step === 3) {
    // Use the exact same logic as the legacy UploadButton
    const handleFileUpload = async (files) => {
      console.log("Selected files", files);
      const file = files[0];
      if (!file) return;

      let isCSV = false;
      let isExcel = false;
      if (file.type === "text/csv") {
        isCSV = true;
      } else if (file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
        isExcel = true;
      }

      if (isCSV) {
        console.log("Parsing CSV file");
        Papa.parse(file, {
          skipEmptyLines: "greedy",
          complete: (results) => {
            let headers = results.data[0];
            let newData = results.data
              .slice(1)
              .map((row) => headers.reduce((acc, cur, i) => Object.assign(acc, { [cur]: row[i] }), {}));
            props.setData((prevData) => prevData.concat(Im.fromJS(newData)).filter((i) => !isEmptyOrAllNull(i)));
            props.setDirty(true);
            setStep(4);
          },
        });
      } else if (isExcel) {
        Swal.fire({
          title: "This is an Excel file",
          text: "Please save the file as CSV file format then reupload it.",
          icon: "error",
          confirmButtonText: "OK",
        });
        // console.log("Parsing XLS file");
        // let { data: newData } = await getExcelData(file);
        // props.setData((prevData) => {
        //   console.log("Adding new data to existing data", prevData.toJS(), newData?.toJS());
        //   return prevData.filter((i) => !isEmptyOrAllNull(i)).concat(newData);
        // });
        // props.setDirty(true);
        // setStep(4);
      }
    };

    return (
      <div>
        <ProgressTracker currentStep={3} />
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="mb-0">Upload your completed CSV file</h5>
        </div>
        <div className="py-4">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <UploadFileDragDrop droppedFileHandler={handleFileUpload} header="dataFile" value="" error={null} />
              <p className="text-muted text-center mt-3 mb-4">Drag and drop your CSV file here, or click to select</p>
            </div>
          </div>

          <div className="d-flex justify-content-start">
            <button className="btn btn-outline-secondary" onClick={() => setStep(2)}>
              <Octicon name="chevron-left" /> Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 4: View and Edit Data
  if (step === 4) {
    return (
      <div>
        <ProgressTracker currentStep={4} />
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">View and edit data</h5>
          <div></div>
        </div>

        <ErrorBoundary>
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <p className="text-muted mb-0">Click "Add" to create new rows then click "Save changes" when finished.</p>
            <div className="d-flex">
              <button className="btn btn-sm btn-outline-secondary mr-2" onClick={handleBackToStep1}>
                <Octicon name="chevron-left" /> Back to start
              </button>
              <ClearButton {...props} />
            </div>
          </div>
        </ErrorBoundary>
      </div>
    );
  }

  return null;
};

const DownloadTemplateButton = (props) => {
  let { templateLink, templateName } = props;

  if (!templateLink) {
    return null;
  }

  return (
    <a className="btn btn-warning font-weight-bold" href={templateLink} download={templateName}>
      <Octicon name="desktop-download" className="mr-2" />
      Download template
    </a>
  );
};

const ClearButton = (props) => {
  let { data, setData, setDirty } = props;

  return (
    <button
      className="btn btn-outline-danger btn-sm d-flex align-items-center"
      onClick={(e) => {
        setData(Im.List());
        setDirty(!!data?.size);
      }}
    >
      <Octicon name="trashcan" className="mr-2" />
      Clear all data
    </button>
  );
};

const UploadButton = (props) => {
  let { setData, data, setDirty, onUploadSuccess } = props;
  const fileInputRef = React.useRef();

  const handleFileChange = async (e) => {
    console.log("Selected files", e.target.files);
    const file = e.target.files[0];
    if (!file) return;

    let isCSV = file.type === "text/csv";
    let isExcel = file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    if (isCSV) {
      console.log("Parsing CSV file");
      Papa.parse(file, {
        skipEmptyLines: "greedy",
        complete: (results) => {
          let headers = results.data[0];
          let newData = results.data
            .slice(1)
            .map((row) => headers.reduce((acc, cur, i) => Object.assign(acc, { [cur]: row[i] }), {}));
          setData((prevData) => prevData.concat(Im.fromJS(newData)).filter((i) => !isEmptyOrAllNull(i)));
          setDirty(true);
          // Call success callback if provided
          if (onUploadSuccess) {
            onUploadSuccess();
          }
        },
      });
    } else if (isExcel) {
      console.log("Parsing Excel file");
      const workbook = new Excel.Workbook();
      await workbook.xlsx.load(file);
      // Try to get the first worksheet (index 0), fallback to worksheets[1] if that fails
      const worksheet = workbook.worksheets[1];
      const headers = worksheet.getRow(1).values.slice(1);
      // Remove headers from sheet now
      worksheet.spliceRows(1, 1);
      const newData = [];
      worksheet.eachRow((row) =>
        newData.push(headers.reduce((acc, cur, i) => Object.assign(acc, { [cur]: row.getCell([i + 1]).value }), {}))
      );
      setData((prevData) => prevData.concat(Im.fromJS(newData)).filter((i) => !isEmptyOrAllNull(i)));
      setDirty(true);
      // Call success callback if provided
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    }
    // Clear the input value to allow re-uploading the same file
    e.target.value = "";
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".csv,.xlsx"
        style={{ display: "none" }}
      />
      <button
        className="btn btn-primary"
        onClick={(e) => {
          e.preventDefault();
          fileInputRef.current.click();
        }}
      >
        <Octicon name="cloud-upload" className="mr-2" />
        Upload file
      </button>
    </>
  );
};

const Explanation = (props) => {
  let { show } = props;

  if (!show) {
    return (
      <>
        <p>Either complete the table below or upload your data from a CSV file:</p>
        <ul>
          <li>
            <b>Step 1</b>: Click the "Upload from CSV" button and upload the CSV
          </li>
          <li>
            <b>Step 2</b>: Press the "Save" button to save the data
          </li>
        </ul>
        <p>
          Please ensure that the CSV file upload has the heading columns that match the table headers below.
          <br />
          Row reference populates automatically from the upload CSV and will help you identify errors within specific
          rows of data.
        </p>
        <p style={{ fontWeight: "bold" }}>
          For manual entry, please add all required information and don't forget to save.
        </p>
      </>
    );
  }

  return (
    <>
      <p>
        Either complete the table below manually by adding a new row for each entry or follow the steps below to upload
        your data from an Excel or CSV file:
      </p>
      <ul>
        <li>
          <b>Step 1</b>: Download the Excel template
        </li>
        <li>
          <b>Step 2</b>: Save the completed Excel template as a CSV or Excel file
        </li>
        <li>
          <b>Step 3</b>: Click the "Upload from file" button and upload the file
        </li>
        <li>
          <b>Step 4</b>: Press the "Save" button to save the data
        </li>
      </ul>
      <p>
        Please ensure that the file upload has the heading columns that match the table headers below. <br />
        Row reference populates automatically from the upload and will help you identify errors within specific rows of
        data.
      </p>
      <p style={{ fontWeight: "bold" }}>
        For manual entry, please add all information needed. You can click “Add” to add another data entry row. Please
        click “Save” before closing the box.
      </p>
    </>
  );
};

const clearFile = (props) => {
  let { trackerId, commitId, docStatus, fetches } = props;
  let path = docStatus.get("_path");
  return doFetch(`/api/tracker/${trackerId}/commit/${commitId}/node/${path}/`, "PATCH", {
    type: "documentstatus",
    node: {
      documentstatus: {
        current_object: null,
      },
    },
  })
    .then((r) => r.json())
    .then((newData) => {
      let { data, mutate } = fetches.nodes;
      mutate(mergeNodes(data, newData));
    });
};

const isNullOrEmpty = (v) => v === null || v === undefined || v === "";

export const isEmptyOrAllNull = (data) => {
  return !data || data?.isEmpty() || data.every((value) => isNullOrEmpty(value));
};

export const SaveButton = (props) => {
  let {
    fields,
    data,
    path,
    filename,
    assignments,
    closeModal,
    errors,
    readOnly,
    dirty,
    setDirty,
    currentDocObject,
    setData,
  } = props;
  const [pending, setPending] = useState(false);

  let hasExistingFile = !!currentDocObject?.get("url");
  // Get data with empty rows filtered out
  let _data = data.filter((i) => !isEmptyOrAllNull(i));

  let toolTipMessage = null;
  let hasEmptyRowError = false;
  if (errors?.size) {
    toolTipMessage = <div>Please correct all errors before submitting</div>;
  } else if (!hasExistingFile && _data?.size == 0) {
    toolTipMessage = <div>Add some data</div>;
    hasEmptyRowError = true;
  }

  console.log("SaveButton", hasExistingFile, _data?.toJS());

  return (
    <Tooltip
      className="float-right"
      html={toolTipMessage}
      animateFill={false}
      disabled={readOnly || ((errors.size > 0 ? false : true) && !hasEmptyRowError)}
    >
      <button
        className="btn btn-sm btn-primary "
        disabled={!dirty || errors.size > 0 || hasEmptyRowError}
        onClick={(e) => {
          e.preventDefault();
          if (pending) return null;

          // If there is no data then just clear file upload completely
          if (!_data?.size) {
            setPending(true);
            clearFile(props).then(() => setPending(false));
            return null;
          }

          let text = dataToCSVText({ fields, data: _data });
          uploadFile(textToFormData({ text, path, filename }), {
            ...props,
            setPending,
            saveSuccessHandler: assignments ? saveSuccessHandler : closeModal,
          });

          setData(_data); // Update the data to be without empty rows
          setDirty(false);
        }}
      >
        {pending ? <div className="spinner-border spinner-border-sm" /> : "Save changes"}
      </button>
    </Tooltip>
  );
};

const dataToCSVText = ({ fields, data }) => {
  let headers = fields.map((field) => field.get("header"));
  let lines = [];
  lines.push(headers.join(","));
  data.map((row) => {
    lines.push(headers.map((header) => row.get(header) || "").join(","));
  });
  return lines.join("\n");
};

const textToFormData = ({ text, path, filename = "data.csv" }) => {
  let formData = new FormData();
  const blob = new Blob([text], { type: "text/csv" });
  formData.append("fileobj", blob, filename);
  formData.append("type", "document");
  formData.append("path", path);
  console.log("Packaged csv text as blob in multipart file object", text);
  return formData;
};

const uploadFile = (formData, props) => {
  /* You can send this as a raw file if you set:
    headers: { "content-type": null: "content-disposition": "attachment; filename=upload.jpg" },
    body: this.state.selectedFiles[0]
    */
  let { trackerId, commitId, path, setPending, saveSuccessHandler, fetches, closeModal } = props;

  if (setPending) setPending(true);

  fetcher(`/api/tracker/${trackerId}/commit/${commitId}/node/${path}/`, "PUT", formData).then(({ payload }) => {
    if (setPending) setPending(false);

    if (saveSuccessHandler) {
      let currentDocObject = payload
        .filter((i) => i.get("type") == "documentstatus")
        .first()
        ?.getIn(["node", "documentstatus", "current_object"]);
      console.log("Running saveSucessHandler", currentDocObject?.toJS(), props);
      saveSuccessHandler({
        ...props,
        currentDocObject,
      });
    }

    // Finally mutate the SWR data, doing this earlier messes up the modal due to re-rendering
    let { data, mutate } = fetches.nodes;
    mutate(mergeNodes(data, payload));
  });
};

const saveSuccessHandler = (props) => {
  // Show a toast notification and close the modal
  const { closeModal } = props;

  // Close the modal immediately
  if (closeModal) {
    closeModal();
  }

  // Show a non-blocking toast notification
  Swal.fire({
    toast: true,
    position: "top-end",
    icon: "info",
    title: "Updating... then this page will refresh.",
    showConfirmButton: false,
    showCloseButton: true,
    timer: 12000,
    timerProgressBar: true,
  });

  // Automatically trigger the auto-fill without showing the popup
  fetchAssignToMetricValues(props);
};

const fetchAssignToMetricValues = ({ trackerId, commitId, doc, currentDocObject }) => {
  doFetch(`/api/tracker/${trackerId}/dispatch/`, "POST", {
    type: "CSV_TO_INPUT_VALUES",
    payload: {
      commitId,
      fileId: currentDocObject?.get("uid"),
      docId: doc?.get("uid"),
    },
  }).then((e) => {
    // Automatically refresh the page without showing a confirmation modal
    window.location.reload();
  });
};
